import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CampaignCard, CampaignStat } from '#/entities/campaign'
import type { CampaignFigure } from '#/entities/campaign'
import { CheckIcon } from '#/shared/ui/icons'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '#/shared/ui/dropdown-menu'

import { useCampaignAttribution } from '../model/use-campaign-attribution'

interface CampaignPerformanceCardProps {
  campaignId: string
  figures: CampaignFigure[]
  /** What "booked" is counted against, as the service words it — the card
   *  renders the sentence and the menu changes the number behind it. */
  basis: string
}

/**
 * How many days after a send a booked order still counts as this campaign's.
 *
 * The read behind the tick is gated on this menu opening (see
 * {@link useCampaignAttribution}) — which is why the trigger is a real menu and
 * not a dialog: opening it is the interaction that earns the fetch.
 */
function AttributionMenu({
  campaignId,
  basis,
}: {
  campaignId: string
  basis: string
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const { current, options, onChange, isSaving } = useCampaignAttribution(
    campaignId,
    open,
  )
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={isSaving}
          className="cursor-pointer text-xs font-medium text-campaign-running hover:underline disabled:cursor-wait disabled:opacity-60"
        >
          {basis} · {t('campaigns.detail.actions.change')}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-52">
        <p className="px-2 py-1.5 text-2xs leading-4 text-warm-500">
          {t('campaigns.detail.attribution.hint')}
        </p>
        {options.map((days) => (
          <DropdownMenuItem key={days} onSelect={() => onChange(days)}>
            <span className="flex-1">
              {t('campaigns.detail.attribution.option', { count: days })}
            </span>
            {days === current && (
              <CheckIcon className="size-4 shrink-0 text-blue-500" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function CampaignPerformanceCard({
  campaignId,
  figures,
  basis,
}: CampaignPerformanceCardProps) {
  const { t } = useTranslation()
  return (
    <CampaignCard>
      <div className="flex flex-col gap-3 p-4">
        <div className="flex flex-wrap items-center gap-2.5">
          <h2 className="text-2xs font-medium tracking-wider text-gray-300">
            {t('campaigns.detail.sections.performance')}
          </h2>
          <AttributionMenu campaignId={campaignId} basis={basis} />
        </div>
        <div className="flex flex-wrap items-start gap-y-4">
          {figures.map((figure) => (
            <CampaignStat
              key={figure.key}
              value={figure.value}
              label={figure.label}
              note={figure.note}
              tone={figure.tone}
              noteTone={figure.noteTone}
            />
          ))}
        </div>
      </div>
    </CampaignCard>
  )
}
